import { ReactElement, ReactNode, useEffect, useMemo } from 'react';
import { hotjar } from 'react-hotjar';

import { StyleProvider } from '@ant-design/cssinjs';
import { CacheProvider, ThemeProvider } from '@emotion/react';
import { CDS_ZINDEX } from '@revenuemarket/cds';
import { RevenueMarketFontFamily, thematicColors, theme } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { OverlayProvider, OverlaysRenderer } from '@revenuemarket/overlays';
import { useFullSpinnerStore } from '@revenuemarket/react';
import { FullSpinner, ModalAlert, ModalConfirm } from '@revenuemarket/ui';
import { StyledToastContainer } from '@revenuemarket/ui/Toast';
import detectPlatform, { detectBrowser } from '@revenuemarket/utils/detectPlatform';
import { Mixpanel, initMixpanelInstance } from '@revenuemarket/utils/mixpanel';
import {
  PartnerInfo,
  createPartnerInfoFromApi,
  getPartnerByHostname,
} from '@revenuemarket/utils/partners/getPartnerByHostname';
import { OverlayProvider as VdsOverlayProvider } from '@revenuemarket/vds';
import { Hydrate, HydrateProps, QueryClientProvider } from '@tanstack/react-query';
import { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { ThemeConfig } from 'antd/es/config-provider/context';
import { OverrideToken } from 'antd/es/theme/interface';
import locale from 'antd/locale/ko_KR';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import AppWrapper, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { NextPage } from 'next/types';
import getCompany from '~apis/company/getCompany';
import { TermOffered } from '~apis/dashboard';
import { getMetaData } from '~apis/metaData';
import { getPartners } from '~apis/partners';
import { CompanyUserPermission } from '~apis/role/type';
import { getSettings } from '~apis/settings';
import favicon from '~assets/images/favicon.svg';
import Layout from '~components/ui/layout';
import ScrapeFloatingPanel from '~components/ui/ScrapeFloatingPanel/ScrapeFloatingPanel';
import { DEFAULT_DOCUMENT_TITLE, DEFAULT_PARTNER_DOCUMENT_TITLE } from '~constants/seo';
import { CompanyProvider } from '~contexts/CompanyContext';
import { useGetFinanceInfo } from '~hooks/queries/dashboard/query';
import { useUserPermissions } from '~hooks/queries/role/query';
import useAutoOpenScrapeFloatingPanel from '~hooks/useAutoOpenScrapeFloatingPanel';
import { useCompanyId } from '~hooks/useCompanyId';
import useGlobalEventSource from '~hooks/useGlobalClobeEventSource';
import useSuperUserHook from '~hooks/useSuperUserHook';
import { antdCache } from '~libs/antd';
import { cache } from '~libs/emotion';
import { show } from '~libs/intercome';
import { IntercomProvider } from '~libs/intercome/IntercomProvider';
import InterComScript from '~libs/intercome/script';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { queryClient } from '~libs/queryClient';
import GlobalStyle from '~libs/styles/GlobalStyle';
import { UtmRouterProvider } from '~providers/UtmRouterProvider';
import checkIsUserMobileAgent from '~utils/mobile/checkIsUserMobileAgent';
import { trackNaverPageView } from '~utils/naver-analytics';

import useModalSystemStore from '../store/modal-system';

// TODO: config 하나로 합쳐서 import
// Register all enterprise features
ModuleRegistry.registerModules([AllEnterpriseModule]);

// Mark all grids as using legacy themes
provideGlobalGridOptions({ theme: 'legacy' });
// Register all community features
ModuleRegistry.registerModules([AllCommunityModule]);

// Mark all grids as using legacy themes
provideGlobalGridOptions({ theme: 'legacy' });

import '@revenuemarket/design/css/font.css';
import '@tremor/react/dist/esm/tremor.css';
import '@revenuemarket/design/css/reset.css';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-quartz.css';
import 'dayjs/locale/ko';
import 'react-toastify/dist/ReactToastify.css';
import '~utils/override/forge';
import '../styles/globals.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'overlayscrollbars/overlayscrollbars.css';
import '@revenuemarket/vds/style.css';

const ensurePolyfill = () => {
  return new Promise<void>((resolve) => {
    if (!window.requestIdleCallback) {
      window.requestIdleCallback = function (cb: IdleRequestCallback): number {
        return window.setTimeout(() => {
          const start = Date.now();
          cb({
            didTimeout: false,
            timeRemaining: function () {
              return Math.max(0, 50 - (Date.now() - start));
            },
          });
        }, 1);
      };
    }

    if (!window.cancelIdleCallback) {
      window.cancelIdleCallback = function (id: number) {
        clearTimeout(id);
      };
    }

    resolve();
  });
};

dayjs.extend(quarterOfYear);
dayjs.extend(updateLocale);
dayjs.extend(relativeTime);
dayjs.locale('ko');
dayjs.updateLocale('ko', {
  weekStart: 1,
  yearStart: 4,
});

export const componentTokens: OverrideToken = {
  Typography: {
    fontSize: 14,
    fontSizeHeading5: 16,
  },
  Table: {
    colorBgContainer: 'transparent',
  },
  Drawer: {
    padding: 16,
    paddingLG: 24,
  },
  Alert: {
    paddingContentVerticalSM: 8,
    colorSuccess: '#29BD72',
  },

  Menu: {
    dropdownWidth: 100,
    paddingXS: 12,
    controlHeightLG: 32,
    fontSize: 14,
  },
};

export const themeForClobe: ThemeConfig = {
  algorithm: [antdTheme.compactAlgorithm],
  token: {
    colorInfo: thematicColors.primary.medium,
    colorPrimary: thematicColors.primary.medium,
    colorSuccess: '#29BD72',
    fontFamily: RevenueMarketFontFamily,
    zIndexPopupBase: CDS_ZINDEX.POP_UP,
    fontSizeIcon: 12,
  },
  components: componentTokens,
};

// FIXME: 추후 걷어내기 (하위호환을 위해 남겨둠)
export const mixpanel = new Mixpanel(process.env.MIX_PANEL_ID);
// 모듈 로딩 시점 믹스패널 인스턴스 초기화
if (process.env.MIX_PANEL_ID) {
  initMixpanelInstance(process.env.MIX_PANEL_ID);
}

interface CustomPageProps {
  dehydratedState?: HydrateProps['state'];
  isMobileAgent?: boolean;
  host: string;
  partnerInfo?: PartnerInfo | null;
}

export type NextPageWithLayout<P = NonNullable<unknown> & { host?: string }, IP = P> = NextPage<
  P,
  IP
> & {
  getLayout?: (page: ReactElement, layoutProps: Record<string, unknown>) => ReactNode;
  mobileHeader?: ReactNode;
};

type AppPropsWithLayout = AppProps<CustomPageProps> & {
  Component: NextPageWithLayout;
};

const AnalysisWrapper = ({ children }: React.PropsWithChildren) => {
  const router = useRouter();
  const { data: userPermissionsData } = useUserPermissions();
  const userPermissions = userPermissionsData?.Data?.data?.companyUserPermissions ?? [];
  const inQuireTrigger = router.asPath.includes('inquire=true');

  const session = useSuperUserHook();
  const { companyId } = useCompanyId();
  const userId = session?.data?.userId;

  useAutoOpenScrapeFloatingPanel();

  const { data: financeInfo } = useGetFinanceInfo({
    companyId: companyId ?? '',
    checkPermission: true,
    permissionEnabled: userPermissions.includes(CompanyUserPermission.CAN_MANAGE_CLOBE_FINANCE),
  });

  const payoutLimit = financeInfo?.Data?.data?.items?.find(
    (item) => item?.type === 'TERM_OFFERED',
  ) as TermOffered;

  useEffect(() => {
    if (inQuireTrigger) {
      show();
    }
  }, [inQuireTrigger]);

  useEffect(() => {
    if (payoutLimit?.payoutLimit) {
      mixpanel.setCommonProperties({
        payoutLimit: payoutLimit?.payoutLimit,
        financingTarget: true,
      });
    } else {
      mixpanel.setCommonProperties({
        payoutLimit: 0,
        financingTarget: false,
      });
    }
  }, [payoutLimit]);

  useEffect(() => {
    // Polyfill이 완료된 후 requestIdleCallback 실행
    ensurePolyfill().then(() => {
      requestIdleCallback(
        () => {
          hotjar.initialize(parseInt(process.env.HOT_JAR_ID, 10), 6);
        },
        { timeout: 500 },
      );
    });
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // FIXME: 제거 불가. 제거할 경우 테이블에서의 DropdownEditor 컴포넌트가 열림과 동시에 바로 닫힘.
      event.stopPropagation();
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    async function track() {
      const isSuperUser = Boolean(session.data?.isAdmin);
      const isSupportUser = session.data?.iss === 'SUPPORT';

      if (location?.pathname.includes('/intro')) {
        mixpanel.track(MIXPANEL_EVENT.PARTNERS.PARTNERS_INTRO_PAGE, {
          subPath: location?.pathname,
        });
      }

      if (session?.status === 'authenticated' && !companyId) {
        mixpanel.identify(session?.data?.userId as string);
        mixpanel.setPeople({
          $name: session?.data?.userName,
        });
      }
      // user authenticate 되면 user set
      if (session?.status === 'authenticated' && companyId) {
        const data = await getMetaData(companyId, session?.data?.userId as string);
        const setting = await getSettings(companyId);
        const step = setting?.Data?.data?.onboardingStep;
        const metaData = data.Data;
        // 유저 확인

        requestIdleCallback(
          () => {
            if (!isSuperUser && !isSupportUser) {
              window.hj('identify', companyId, {
                step,
              });
            }
          },
          { timeout: 4000 },
        );

        if (metaData?.data?.user) {
          mixpanel.identify(metaData?.data?.user.userId as string);

          mixpanel.setPeople({
            $name: metaData?.data?.user?.name,
            $email: metaData?.data?.user?.email,
            position: metaData?.data?.user?.position,
          });

          mixpanel.register({
            browser: detectBrowser(),
            platform: detectPlatform(),
            tradesCount: metaData?.data?.company?.numOfTrades,
            tradeStatus: metaData?.data?.company?.tradeStatus,
            checkingAccountsCount: metaData?.data?.company?.numOfCheckingAccounts,
            banksCount: metaData?.data?.company?.numOfBanks,
            certificatesCount: metaData?.data?.company?.numOfCertificates,
            dqBank: metaData?.data?.company?.dataQualifications?.bank,
            companyId: metaData.data.company.companyId,
          });
          mixpanel.track(MIXPANEL_EVENT.VIEW.DEFAULT, {
            subPath: location?.pathname,
          });

          // 최근 3일 이내 가입한 사용자에게만 Session Replay 활성화
          if (!isSuperUser && !isSupportUser) {
            try {
              const companyData = await getCompany({ companyId });
              const createdAt = companyData?.Data?.data?.createdAt;
              if (createdAt) {
                const isRecentUser = dayjs().diff(dayjs(createdAt), 'day') <= 3;
                if (isRecentUser) {
                  mixpanel.startSessionRecording();
                }
              }
            } catch (e) {
              // Session Replay 활성화 실패 시 무시
            }
          }
        } else {
          mixpanel.track(MIXPANEL_EVENT.USER.CHECK_USER_METADATA, {
            hasMetadata: Boolean(metaData?.data),
            metaUserId: metaData?.data?.user?.userId,
            userId: session?.data?.userId,
            companyId,
          });
        }
      }
    }

    track();
  }, [Boolean(session?.data), companyId]);

  useEffect(() => {
    const handleRouteChange = () => {
      async function track() {
        if (session?.status === 'authenticated') {
          mixpanel.track(MIXPANEL_EVENT.VIEW.DEFAULT);
        }
        // 네이버 애널리틱스 페이지뷰 추적
        trackNaverPageView();
      }
      track();
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, session?.status]);

  return children;
};

const GlobalEventSource = () => {
  useGlobalEventSource();
  return null;
};

function App({
  Component,
  pageProps: { dehydratedState, isMobileAgent, host, partnerInfo, ...pageProps },
}: AppPropsWithLayout) {
  const { alert, confirm, closeAlert, closeConfirm } = useModalSystemStore();
  const { isShow, message } = useFullSpinnerStore();

  const isMobileTarget = isMobileAgent;

  // 파트너 정보는 각 컴포넌트에서 필요할 때만 가져오도록 수정
  const isPartnerIntro = !!partnerInfo; // 기본값으로 설정
  const showIntercom = !isPartnerIntro;

  // KEYBOARD로 ctrl+c, ctrl+v 하는 이벤트가 잡히면 특정 함수 호출 (mac, window 둘다 가능)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCopy = (e.ctrlKey || e.metaKey) && e.key === 'c';
      const isPaste = (e.ctrlKey || e.metaKey) && e.key === 'v';

      if (isCopy) {
        mixpanel.track(MIXPANEL_EVENT.CLIPBOARD.COPY, {
          page: window.location.pathname,
        });
        mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
          type: MIXPANEL_EVENT.CLIPBOARD.COPY,
        });
      }

      if (isPaste) {
        mixpanel.track(MIXPANEL_EVENT.CLIPBOARD.PASTE, {
          page: window.location.pathname,
        });
        mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
          type: MIXPANEL_EVENT.CLIPBOARD.PASTE,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const getLayout =
    Component.getLayout ||
    ((page) => {
      return (
        <Layout mobileHeader={Component.mobileHeader} isMobile={isMobileTarget} host={host}>
          {page}
        </Layout>
      );
    });
  const metaTitle = useMemo(() => {
    return isPartnerIntro ? DEFAULT_PARTNER_DOCUMENT_TITLE : DEFAULT_DOCUMENT_TITLE;
  }, [isPartnerIntro]);

  return (
    <QueryClientProvider client={queryClient}>
      <UtmRouterProvider>
        <Hydrate state={dehydratedState}>
          <CompanyProvider>
            <AnalysisWrapper>
              <IntercomProvider>
                <ThemeProvider theme={theme}>
                  <VdsOverlayProvider>
                    <OverlayProvider>
                      <Head>
                        <title>{metaTitle}</title>
                        <meta name="format-detection" content="telephone=no, email=no" />
                        <meta
                          name="viewport"
                          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
                        />
                      </Head>
                      {isPartnerIntro ? (
                        <Head>
                          <meta
                            property="og:image"
                            content={`https://static.clobe.ai/image/app-partner-og-image.png`}
                          />
                          <meta property="og:url" content={host} />
                          <meta
                            property="og:title"
                            content={`${partnerInfo.title}, 자금 관리도 실시간으로`}
                          />
                        </Head>
                      ) : (
                        <Head>
                          <link rel="shortcut icon" type="image/x-icon" href={favicon.src} />
                          <link rel="icon" type="image/x-icon" href={favicon.src} />
                          <link rel="apple-touch-icon" type="image/x-icon" href={favicon.src} />
                        </Head>
                      )}

                      <GlobalStyle />
                      <Script
                        id="kakao-js-sdk"
                        src="https://t1.kakaocdn.net/kakao_js_sdk/2.7.5/kakao.min.js"
                        integrity="sha384-dok87au0gKqJdxs7msEdBPNnKSRT+/mhTVzq+qOhcL464zXwvcrpjeWvyj1kCdq6"
                        crossOrigin="anonymous"
                        strategy="afterInteractive"
                      />
                      <Script
                        id="GTM"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                          __html: `
      (function(w,d,s,l,i){
        w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});

        var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),
            dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;
        j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);

        // Ensure dataLayer is available before pushing
        if (window[l] && Array.isArray(window[l])) {
          window[l].push({'event': 'datalayer-checker'});
        }
      })(window,document,'script','dataLayer','GTM-W3KXVQF6');
    `,
                        }}
                      />

                      {/* gtag alias */}
                      <Script
                        id="gtag-alias"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
          `,
                        }}
                      />
                      <Script
                        id="naver-wcs"
                        type="text/javascript"
                        src="//wcs.naver.net/wcslog.js"
                        onLoad={() => {
                          trackNaverPageView();
                        }}
                      />

                      <Script
                        id="fb-pixel"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '1711542192551723');
            fbq('init', '303011406160446');
            fbq('track', 'PageView');
          `,
                        }}
                      />
                      {showIntercom && <InterComScript />}

                      <div id="captureContainer" />
                      <CacheProvider value={cache}>
                        <StyleProvider cache={antdCache}>
                          {getLayout(<Component {...pageProps} host={host} />, { host })}
                        </StyleProvider>
                      </CacheProvider>
                      {alert.isOpen && <ModalAlert handleClose={closeAlert} {...alert} />}
                      {confirm.isOpen && <ModalConfirm handleClose={closeConfirm} {...confirm} />}
                      {isShow && <FullSpinner message={message} />}
                      <StyledToastContainer newestOnTop limit={1} />
                      <ScrapeFloatingPanel />
                      <ConfigProvider locale={locale} theme={{ ...themeForClobe }}>
                        <OverlaysRenderer />
                      </ConfigProvider>
                    </OverlayProvider>
                  </VdsOverlayProvider>
                </ThemeProvider>
              </IntercomProvider>
              <GlobalEventSource />
            </AnalysisWrapper>
          </CompanyProvider>
        </Hydrate>
      </UtmRouterProvider>
    </QueryClientProvider>
  );
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await AppWrapper.getInitialProps(appContext);
  const userAgent = appContext.ctx.req?.headers['user-agent'] || '';
  // host 정보 추출
  const host =
    appContext.ctx.req?.headers.host ||
    appContext.ctx.req?.headers['x-forwarded-host'] ||
    'localhost';

  const isMobileAgent = checkIsUserMobileAgent(userAgent);

  // 서버사이드에서 파트너 정보 미리 가져오기
  let partnerInfo: PartnerInfo | null = null;
  try {
    const response = await getPartners();
    if (response.Status === ResponseStatus.SUCCESS && response.Data) {
      const partnerMap = createPartnerInfoFromApi(response.Data.data.partners || []);
      partnerInfo = getPartnerByHostname(Array.isArray(host) ? host[0] : host, partnerMap);
    }
  } catch (error) {
    console.error('Failed to fetch businessPartners in getInitialProps:', error);
  }

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      isMobileAgent,
      host,
      partnerInfo,
    },
  };
};

export default App;
